import "./backgroundTimeline.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { faPlay, faPause } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { throttle, range } from "lodash";
import {
  parse,
  format,
  getWeek,
  eachDayOfInterval,
  startOfDay,
  endOfDay,
  isSameDay,
  isSameHour,
} from "date-fns";
import { ThemeProvider, useTheme } from "../../theme/ThemeContext";

// Define types
//---------------------------
interface TimelineElement {
  id: string;
  stimestamp: number;
  Interval: number;
  edtimestamp: number;
}

interface ProcessedTimelineElement {
  layerid: number | string;
  generatedTimestamp: number;
  date: Date;
  day: number;
  month: number;
  year: number;
  hour: number;
  minute: number;
  second: number;
  week: number;
  interval: number;
  weekDay: string;
}

interface BackgroundMapTimelineProps {
  passSelectedTimeSlotValue: (value: string) => void;
  passSelectedDateSlotValue: (value: string) => void;
  timelineDataArray:TimelineElement[];
  getVoiceRecognitionValue:string;
  isSatelightClick:string;
  satelliteTime:string[];
  // From?: string;
  // To?: string;
  // activeLayers: string[]; 
  // passTimeIndexCount: (value: number) => void;
  // selectModel:string;
  // getValueFromLefSideMenu:string;
  // openLayerTypeMap: Map<string, boolean>;
}

const BackgroundMapTimeline: React.FC<BackgroundMapTimelineProps> = ({
  passSelectedTimeSlotValue,
  passSelectedDateSlotValue,
  timelineDataArray,
  getVoiceRecognitionValue,
  isSatelightClick,
  satelliteTime
  // From,
  // To,
  // activeLayers,
  // passTimeIndexCount,
  // selectModel,
  // getValueFromLefSideMenu,
  // openLayerTypeMap
}) => {
  const [playTimer, setPlayTimer] = useState(false);
  const [playInterval, setPlayInterval] = useState<NodeJS.Timeout | null>(null);
  const timeSlotSlideRef = useRef<HTMLDivElement>(null);
  const dateSlotSlideRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { theme } = useTheme();
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [selectedTimeSlotIndex, setSelectedTimeSlotIndex] = useState(0);
  const [startPosition, setStartPosition] = useState<number | null>(null);
  const [draggingDistance, setDraggingDistance] = useState(0);
  const dragThreshold = 5;

  var timeLineType = "satellite";

    useEffect(() => {}, [isSatelightClick]);

  const datetimeSet = [
    {
      satellite: [
        "2025-01-09 13:22:44zzutc",
        "2025-01-08 12:43:44zzutc",
        "2025-01-10 00:35:44zzutc",
        "2025-01-10 02:33:44zzutc",
        "2025-01-10 04:15:44zzutc"
      ],
      // airQuality: [
      //   "2025-01-09 13:22:44zzutc",
      //   "2025-01-09 12:22:44zzutc",
      //   "2025-01-10 00:22:44zzutc",
      //   "2025-01-10 02:22:44zzutc",
      //   "2025-01-10 04:22:44zzutc"
      // ]
    }
  ];

  // Function to format the date range for the week
  const getWeekRange = (date: Date) => {
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
  
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); 
  
    return `${startOfWeek.getMonth() + 1}/${startOfWeek.getDate()}/${startOfWeek.getFullYear()} - ${endOfWeek.getMonth() + 1}/${endOfWeek.getDate()}/${endOfWeek.getFullYear()}`;
  };
  
  const sortedData = satelliteTime!
    .map(dateString => new Date(dateString.replace("zzutc", "Z")))
    .sort((a, b) => a.getTime() - b.getTime());
  
  const transformedData = sortedData.map((date, index) => ({
    date,
    day: date.getDate(),
    generatedTimestamp: Math.floor(date.getTime() / 1000), // Convert to UNIX timestamp
    hour: date.getHours(),
    interval: 10800,
    layerid: "ecmwf3h",
    minute: date.getMinutes(),
    month: date.getMonth(),
    second: date.getSeconds(),
    week: Math.ceil(date.getDate() / 7),
    weekDay: getWeekRange(date),
    year: date.getFullYear()
  }));
  
  // Convert sorted array to hashmap
  const hashMap = transformedData.reduce((acc, item, index) => {
    acc[index + 1] = item;
    return acc;
  }, {} as Record<number, typeof transformedData[0]>);


  /* const [timelinehashMap, setTimelinehashMap] = useState<{
    [key: number]: ProcessedTimelineElement;
  }>({}); */


const toDay = new Date();
const currentTime = `${String(toDay.getHours()).padStart(2, '0')}:${String(toDay.getMinutes()).padStart(2, '0')}`;

const weekday = toDay.toLocaleDateString('en-US', { weekday: 'short' });
const month = toDay.toLocaleDateString('en-US', { month: 'short' });
const day = String(toDay.getDate()).padStart(2, '0'); // Add leading zero to the day
const year = toDay.getFullYear();

// Construct the date string
const currentDate = `${weekday} ${month} ${day} ${year}`;

// Output: Thu Dec 05 2024

const timelinehashMap = useMemo(() => {

  if ((!timelineDataArray || timelineDataArray.length === 0) && (isSatelightClick !=="satelliteClick")) return {};

  const sortedArray = [...timelineDataArray].sort(
    (a, b) => a.stimestamp - b.stimestamp
  );

  let timelineIndex = 0;
  const timelineMapNew: { [key: number]: ProcessedTimelineElement } = {};
  const seenTimestamps = new Set<string>(); // To track unique timestamps

  sortedArray.forEach((currentElement) => {
    const currentStart = currentElement.stimestamp;
    const currentEnd = currentElement.edtimestamp;
    const currentInterval = currentElement.Interval;

    for (let ts = currentStart; ts <= currentEnd; ts += currentInterval) {
      const cnvtdate = new Date(ts * 1000); 
      const uniqueKey = `${cnvtdate.getFullYear()}-${cnvtdate.getMonth()}-${cnvtdate.getDate()}-${cnvtdate.getUTCHours()}-${cnvtdate.getUTCMinutes()}-${cnvtdate.getUTCSeconds()}`;
     
      if (!seenTimestamps.has(uniqueKey)) {
    
        timelineIndex++;
        seenTimestamps.add(uniqueKey);

        timelineMapNew[timelineIndex] = {
        
          layerid: currentElement.id,
          generatedTimestamp: ts,
          interval: currentInterval,
          date: cnvtdate,
          day: cnvtdate.getDate(),
          month: cnvtdate.getMonth(),
          year: cnvtdate.getFullYear(),
          hour: cnvtdate.getUTCHours(),
          minute: cnvtdate.getUTCMinutes(),
          second: cnvtdate.getUTCSeconds(),
          week: getWeek(cnvtdate),
          weekDay: `${new Date(currentStart * 1000).toLocaleDateString()} - ${new Date(currentEnd * 1000).toLocaleDateString()}`,
        };
      }
    }
  });


  return isSatelightClick === "satelliteClick" ? hashMap : timelineMapNew;
}, []);

  useEffect(() => {
  if(getVoiceRecognitionValue==="play timeline" ||getVoiceRecognitionValue ==="stop timeline"){
    startAutoPlay();
  }
    }, [getVoiceRecognitionValue]);

  const groupedByDate = () => {
    const grouped: { [key: string]: ProcessedTimelineElement[] } = {};

    // Iterate over the timelinehashMap and group entries by date or week
    Object.values(timelinehashMap).forEach((entry) => {
      let key = "";

      if (entry.interval >= 86400) {
        // Group by week
        key = `Week ${entry.week}`;
      } else {
        key = entry.date.toDateString();
      }

      if (!grouped[key]) {
        grouped[key] = [];
      }

      grouped[key].push(entry);
    });

    return grouped;
  };

  useEffect(() => {
    const firstMatchingIndex = Object.keys(timelinehashMap).find((key) => {
      const entryDate = timelinehashMap[parseInt(key)].date.toDateString();
      return entryDate === currentDate;
    });

    if (firstMatchingIndex) {
      const firstIndex = parseInt(firstMatchingIndex);
      setSelectedTimeSlotIndex(firstIndex - 1);
      const selectedElement = timelinehashMap[firstIndex];

      if (timeSlotSlideRef.current) {
        timeSlotSlideRef.current.scrollTo({
          left: (firstIndex - 1) * 60,
          behavior: "smooth",
        });
      }

      const selectedDate = timelinehashMap[firstIndex].date.toDateString();
      const groupedDates = Object.keys(groupedByDate());

      let dateSlotIndex = groupedDates.findIndex(
        (date) => date === selectedDate
      );

      if (dateSlotIndex === -1) {
        dateSlotIndex = 0;
      }

      if (dateSlotSlideRef.current) {
        dateSlotSlideRef.current.scrollTo({
          left: dateSlotIndex * 160,
          behavior: "smooth",
        });
      }

      passSelectedDateSlotValue(
        selectedElement.interval >= 86400
          ? selectedElement.weekDay
          : dateConverting(selectedElement.date.toDateString())
      );
      passSelectedTimeSlotValue(
        `${selectedElement.date
          .getHours()
          .toString()
          .padStart(2, "0")}:${selectedElement.date
          .getMinutes()
          .toString()
          .padStart(2, "0")}`
      );
    }else{
      const selectedElement = timelinehashMap[1];
  
      setSelectedTimeSlotIndex(0);
        selectedDateorTimeSlot(
          dateSlotSlideRef,
          0,
          160,
          "smooth"
        );
        //const selectedElement2 = timelinehashMap[1];
        //console.log("---304---",selectedElement2.date)
        passSelectedDateSlotValue(
          selectedElement.interval >= 86400
            ? selectedElement.weekDay
            : dateConverting(selectedElement.date.toDateString())
        );
        passSelectedTimeSlotValue(
          `${selectedElement.date
            .getHours()
            .toString()
            .padStart(2, "0")}:${selectedElement.date
            .getMinutes()
            .toString()
            .padStart(2, "0")}`
        );
    }
  }, [ timelinehashMap]);

  useEffect(() => {
    const [currentHours, currentMinutes] = currentTime.split(":").map(Number);

    const hasCurrentDate = Object.values(timelinehashMap).some(entry => 
      isSameDay(entry.date, currentDate)
    );
  
    // Calculate the target timestamp for the current time
    const targetTime = new Date(currentDate);
    targetTime.setHours(currentHours, currentMinutes, 0, 0);
    const targetTimestamp = Math.floor(targetTime.getTime() / 1000);
  
    // Find the nearest timeslot to the target timestamp
    let closestIndex = 0;
    let smallestDifference = Infinity;
  
    Object.keys(timelinehashMap).forEach((key) => {
      const entryTimestamp = timelinehashMap[parseInt(key)].generatedTimestamp;
      const difference = Math.abs(entryTimestamp - targetTimestamp);
  
      if (difference < smallestDifference) {
        smallestDifference = difference;
        closestIndex = parseInt(key);
      }
    });
  
    // Set the nearest time slot as the selected slot
    if (closestIndex > 0) {
      if(!hasCurrentDate){
        const selectedElement = timelinehashMap[1];
        setSelectedTimeSlotIndex(0);
        passSelectedDateSlotValue(
          selectedElement.interval >= 86400
            ? selectedElement.weekDay
            : dateConverting(selectedElement.date.toDateString())
        );
        passSelectedTimeSlotValue(
          `${selectedElement.date
            .getHours()
            .toString()
            .padStart(2, "0")}:${selectedElement.date
            .getMinutes()
            .toString()
            .padStart(2, "0")}`
        );
      }else{
        setSelectedTimeSlotIndex(closestIndex - 1);
      const selectedElement = timelinehashMap[closestIndex];
  
      if (timeSlotSlideRef.current) {
        timeSlotSlideRef.current.scrollTo({
          left: (closestIndex - 1) * 60,
          behavior: "smooth",
        });
      }
  
      passSelectedDateSlotValue(
        selectedElement.interval >= 86400
          ? selectedElement.weekDay
          : dateConverting(selectedElement.date.toDateString())
      );
      passSelectedTimeSlotValue(
        `${selectedElement.date
          .getHours()
          .toString()
          .padStart(2, "0")}:${selectedElement.date
          .getMinutes()
          .toString()
          .padStart(2, "0")}`
      );

      }
      
    }
  }, [timelinehashMap]);


  const handleMouseDownDateandTime = (
    e: React.MouseEvent,
    slideRef: React.RefObject<HTMLDivElement>
  ) => {
    if (slideRef.current) {
      setIsDragging(true);
      setStartX(e.pageX - slideRef.current.offsetLeft);
      setScrollLeft(slideRef.current.scrollLeft);
      setDraggingDistance(0);
    }
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeaveDate = () => {
    setIsDragging(false);
  };

  const handleMouseUpDate = () => {
    setIsDragging(false);
  };

  const handleMouseMoveDateandTime = (
    e: React.MouseEvent,
    slideRef: React.RefObject<HTMLDivElement>
  ) => {
    if (!isDragging || !slideRef.current) return;
    const x = e.pageX - slideRef.current.offsetLeft;
    const walk = (x - startX) * 1; // Increase the multiplier for faster drag
    slideRef.current.scrollLeft = scrollLeft - walk;

    setDraggingDistance(Math.abs(x - startX));
  };

  const selectFirstTimeSlotOfDate = (selectedDate: string) => {
    const groupedTimeline = groupedByDate();
    const firstTimeSlotForDate = groupedTimeline[selectedDate]?.[0];
 
    //passSelectedDateSlotValue( dateConverting(selectedDate));
    passSelectedDateSlotValue(
      selectedDate.includes("Week")
        ? firstTimeSlotForDate.weekDay
        : dateConverting(selectedDate)
    );
    passSelectedTimeSlotValue(
      `${firstTimeSlotForDate.date
        .getHours()
        .toString()
        .padStart(2, "0")}:${firstTimeSlotForDate.date
        .getMinutes()
        .toString()
        .padStart(2, "0")}`
    );
    if (firstTimeSlotForDate) {
      const firstTimeSlotIndex = Object.keys(timelinehashMap).findIndex(
        (key) =>
          timelinehashMap[parseInt(key)].generatedTimestamp ===
          firstTimeSlotForDate.generatedTimestamp
      );
      setSelectedTimeSlotIndex(firstTimeSlotIndex);
      if (firstTimeSlotIndex !== -1) {
        if (timeSlotSlideRef.current) {
          if (draggingDistance < dragThreshold) {
            timeSlotSlideRef.current.scrollTo({
              left: firstTimeSlotIndex * 60,
              behavior: "smooth",
            });
          } else {
            timeSlotSlideRef.current.scrollTo({
              left: firstTimeSlotIndex * 60,
              behavior: "auto",
            });
          }
        }
      }
    }
  };

  const handleScrollandDragNew = (
    slideRef: React.RefObject<HTMLDivElement>,
    size: number
  ) => {
    if (slideRef.current && draggingDistance > dragThreshold) {
      const currentScrollLeft = slideRef.current.scrollLeft;
      const nearestItem = Math.round(currentScrollLeft / size);

      if (slideRef === timeSlotSlideRef) {
        const selectedElement = timelinehashMap[nearestItem + 1];
        passSelectedDateSlotValue(
          selectedElement.interval >= 86400
            ? selectedElement.weekDay
            : dateConverting(selectedElement.date.toDateString())
        );
        passSelectedTimeSlotValue(
          `${selectedElement.date
            .getHours()
            .toString()
            .padStart(2, "0")}:${selectedElement.date
            .getMinutes()
            .toString()
            .padStart(2, "0")}`
        );
        setSelectedTimeSlotIndex(nearestItem);
        if (selectedElement) {
          if (selectedElement.interval >= 86400) {
            const selectedDate = selectedElement.week.toString();
            const currentDateSlotIndex = Object.keys(groupedByDate()).findIndex(
              (week) => week === `Week ${selectedDate}`
            );
            if (currentDateSlotIndex !== -1) {
              selectedDateorTimeSlot(
                dateSlotSlideRef,
                currentDateSlotIndex,
                160,"smooth"
              );
            }
          } else {
            const selectedDate = selectedElement.date.toDateString();
        
            const currentDateSlotIndex = Object.keys(groupedByDate()).findIndex(
              (date) => date === selectedDate
            );
            if (currentDateSlotIndex !== -1) {
              selectedDateorTimeSlot(
                dateSlotSlideRef,
                currentDateSlotIndex,
                160,"smooth"
              );
            }
          }
        }

        if (timeSlotSlideRef.current) {
          timeSlotSlideRef.current.scrollTo({
            left: nearestItem * size,
            behavior: "smooth",
          });
        }
      }
      if (slideRef === dateSlotSlideRef) {
        const selectedDate = Object.keys(groupedByDate())[nearestItem];
        selectFirstTimeSlotOfDate(selectedDate);
        if (dateSlotSlideRef.current) {
          dateSlotSlideRef.current.scrollTo({
            left: nearestItem * size,
            behavior: "smooth",
          });
        }
      }
    }
  };

  const selectedDateorTimeSlot = (
    slideRef: React.RefObject<HTMLDivElement>,
    index: number,
    size: number,
    behavior: ScrollBehavior | undefined
  ) => {
    if (slideRef.current) {
      const nearestItem = index;
      if (slideRef === timeSlotSlideRef) {
        setSelectedTimeSlotIndex(nearestItem);

        const selectedElement = timelinehashMap[index + 1];
        /* passSelectedDateSlotValue(
          dateConverting(selectedElement.date.toDateString())
        ); */
        passSelectedDateSlotValue(
          selectedElement.interval >= 86400
            ? selectedElement.weekDay
            : dateConverting(selectedElement.date.toDateString())
        );
        passSelectedTimeSlotValue(
          `${selectedElement.date
            .getHours()
            .toString()
            .padStart(2, "0")}:${selectedElement.date
            .getMinutes()
            .toString()
            .padStart(2, "0")}`
        );
        if (selectedElement) {
          if (selectedElement.interval >= 86400) {
            const selectedDate = selectedElement.week.toString();
            const currentDateSlotIndex = Object.keys(groupedByDate()).findIndex(
              (week) => week === `Week ${selectedDate}`
            );
            if (currentDateSlotIndex !== -1) {
              selectedDateorTimeSlot(
                dateSlotSlideRef,
                currentDateSlotIndex,
                160,"smooth"
              );
            }
          } else {
            const selectedDate = selectedElement.date.toDateString();
         
            const currentDateSlotIndex = Object.keys(groupedByDate()).findIndex(
              (date) => date === selectedDate
            );
            if (currentDateSlotIndex !== -1) {
              selectedDateorTimeSlot(
                dateSlotSlideRef,
                currentDateSlotIndex,
                160,"smooth"
              );
            }
          }
        }
        slideRef.current.scrollTo({
          left: index * size,
          behavior: behavior,
        });
      }else {
        if (draggingDistance < dragThreshold) {
          slideRef.current.scrollTo({
            left: index * size,
            behavior: "smooth",
          });
        } else {
          slideRef.current.scrollTo({
            left: index * size,
            behavior: "auto",
          });
        }
      }
     
    }
  };


  const selectedDateSlot = (index: number) => {
    //if (!isScrolling || !isDragging) {
    if (dateSlotSlideRef.current) {
      const selectedDate = Object.keys(groupedByDate())[index];
      selectFirstTimeSlotOfDate(selectedDate);
      dateSlotSlideRef.current.scrollTo({
        left: index * 160,
        behavior: "smooth",
      });
    }
    //}
  };

  const startAutoPlay = () => {
    if (!playTimer) {
      const interval = setInterval(() => {
        const maxIndex = Object.keys(timelinehashMap).length -1;
        setSelectedTimeSlotIndex((prevIndex) => {
          const newIndex = prevIndex + 1;
          if (newIndex > maxIndex) {
            clearInterval(playInterval!);
            setPlayTimer(true);
            selectedDateorTimeSlot(timeSlotSlideRef, 0, 60, "auto");
            return 0;
          }
          selectedDateorTimeSlot(timeSlotSlideRef, newIndex, 60, "auto");
          return newIndex;
        });
      }, 2000);

      setPlayInterval(interval);
      setPlayTimer(true);
    } else {
      clearInterval(playInterval!);
      setPlayTimer(false);
}};

  const dateConverting = (dateString: string): string => {
    const parsedDate = parse(dateString, "EEE MMM d yyyy", new Date());
    const formattedDate = format(parsedDate, "yyyy-MM-dd");
    return formattedDate;
  };
  
  const weekDateConverting = (dateString: string) => {
    const date = new Date(dateString);
    
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    
    return date.toLocaleDateString('en-US', options).replace(',', '/');
  };

  const formatWeekDateRange = (dateRangeString: string) => {
    // Split the date range into start and end date
    const [startDateString, endDateString] = dateRangeString.split(' - ');
  
    const startDate = new Date(startDateString);
    const endDate = new Date(endDateString);
  
    const formattedStartDate = weekDateConverting(startDateString);
    const formattedEndDate = weekDateConverting(endDateString);
    
    // Check if the month is the same; if so, show the month only once
    const startMonth = startDate.toLocaleString('en-US', { month: 'short' });
    const endMonth = endDate.toLocaleString('en-US', { month: 'short' });
    
    if (startMonth === endMonth) {
      return `${startMonth} / ${startDate.getDate()} - ${endMonth} / ${endDate.getDate()}`;
    } else {
      return `${formattedStartDate} - ${formattedEndDate}`;
    }
  };

  return (
    <div className={`topfixed-timeline-container ${theme}`}>
      <div className="timeline-btns-container">
        <div className="timeline-btns" onClick={startAutoPlay}       onTouchStart={startAutoPlay}>
          <FontAwesomeIcon size="xl" icon={playTimer ? faPause : faPlay} color={playTimer?"orange":"rgb(76, 187, 23)"}/>
        </div>
      </div>
      <div className="timeline-wrap-container">
        <div className="timeline-time-bg-container">
          <div
            className="timeline-timeslideC"
            onMouseDown={(e) => handleMouseDownDateandTime(e, timeSlotSlideRef)}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={(e) => handleMouseMoveDateandTime(e, timeSlotSlideRef)}
            onScroll={() => handleScrollandDragNew(timeSlotSlideRef, 60)}
            ref={timeSlotSlideRef}
          >
            <div className="timeline-selected-timeslot">
              <div className="timeline-selected-timeslot-marker"></div>
            </div>
            {Object.entries(timelinehashMap).map(([key, entries]) => (
              <div
                key={key}
                onClick={() => {
                  if (draggingDistance < dragThreshold) {
                    selectedDateorTimeSlot(
                      timeSlotSlideRef,
                      parseInt(key) - 1,
                      60,
                      "smooth"
                    );
                  }
                }}
                onTouchStart={() => {
                  if (draggingDistance < dragThreshold) {
                    selectedDateorTimeSlot(
                      timeSlotSlideRef,
                      parseInt(key) - 1,
                      60,
                      "smooth"
                    );
                  }
                }}
                className="time-slots"
              >
                <div
                  className={
                    selectedTimeSlotIndex + 1 === parseInt(key)
                      ? "time-slots-marker-active"
                      : "time-slots-marker"
                  }
                ></div>
                {/* <div> */}
                {`${entries.date
                  .getHours()
                  .toString()
                  .padStart(2, "0")}:${entries.date
                  .getMinutes()
                  .toString()
                  .padStart(2, "0")}`}
                {/* </div> */}
              </div>
            ))}
          </div>
        </div>
        {/* --------------------Date-Slots------------------ */}
        <div className="timeline-date-bg-container">
          <div
            className="timeline-dateslideC"
            onMouseDown={(e) => handleMouseDownDateandTime(e, dateSlotSlideRef)}
            onMouseLeave={handleMouseLeaveDate}
            onMouseUp={handleMouseUpDate}
            onMouseMove={(e) => handleMouseMoveDateandTime(e, dateSlotSlideRef)}
            onScroll={() => handleScrollandDragNew(dateSlotSlideRef, 160)}
            ref={dateSlotSlideRef}
          >
            <div className="timeline-selected-dateslot"></div>
            {Object.entries(groupedByDate()).map(([date, entries], index) => (
              <div
                onClick={() => {
                  if (draggingDistance < dragThreshold) {
                    /* console.log("interval: ", entries[0].weekDay);
                    console.log("Dateindex: ",); */
                    selectedDateSlot(index);
                  }
                }}
                onTouchStart={() => {
                  if (draggingDistance < dragThreshold) {
                    /* console.log("interval: ", entries[0].weekDay);
                    console.log("Dateindex: ",); */
                    selectedDateSlot(index);
                  }
                }}
                key={index}
                className="date-slots"
              >
                {entries[0].interval >= 86400
                  ? formatWeekDateRange(entries[0].weekDay)
                  : date}
                  
                {date === currentDate && (
                  <div style={{ fontSize: "12px" }}>Today</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundMapTimeline;
